/**
 * Redirect-path validation.
 *
 * OPEN-REDIRECT PROTECTION. This module is the boundary between a path the
 * application chose and a path an attacker supplied via `?redirect=`. Every
 * post-login return path passes through `safeRedirectPath`.
 *
 * The rule is ALLOW-LIST BY CONSTRUCTION, not deny-list by inspection: a value is
 * returned only if it parses as a same-origin ABSOLUTE PATH. Anything with a
 * scheme, a host, a protocol-relative prefix, a backslash, or traversal is
 * discarded. Deny-listing `http://` and `//` would miss the next variant
 * (`/\evil.com`, `https:/\evil.com`, encoded forms); allow-listing does not.
 */

/**
 * True when the string contains a character that must never appear in a
 * return path.
 *
 * Written as explicit code-point comparisons rather than a regex character
 * class on purpose. A class here would have to embed literal control
 * characters (U+0000-U+001F), which makes the source file binary and
 * invisible in review. This form is reviewable, and it states the three
 * exclusions explicitly:
 *
 *   - backslash: browsers normalise `/\` to `//`, restoring an authority form
 *   - C0 controls: can be used to split a value in intermediaries that
 *     log or rewrite it
 *   - DEL (0x7f): the classic header-injection terminator
 *
 * A HYPHEN is deliberately allowed. It is a legal path character and appears in
 * most real routes.
 */
function hasUnsafeCharacter(value: string): boolean {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (char === '\\' || code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

/**
 * Returns `raw` if it is a safe same-origin path, otherwise `fallback`.
 *
 * Accepts: `/orders`, `/orders?page=2`, `/orders#top`, `/orders/line-items`
 * Rejects: `https://evil.com`, `//evil.com`, `/\evil.com`, `javascript:alert(1)`,
 *          a path with surrounding whitespace, any non-string
 *
 * Traversal (`/../admin`) is NORMALISED to `/admin`, not rejected — it never
 * leaves the origin, so it is not an open redirect. What matters is that the
 * attacker-controlled string is not what gets returned.
 */
export function safeRedirectPath(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback;

  // Reject rather than trim: a path with surrounding whitespace is not a path we
  // emitted, and trimming would silently "repair" attacker-supplied input.
  if (raw.length === 0 || raw !== raw.trim()) return fallback;
  if (hasUnsafeCharacter(raw)) return fallback;

  // Must be root-relative. This alone excludes `scheme:` and `//host` forms,
  // because both begin with a character other than `/`.
  if (!raw.startsWith('/')) return fallback;

  // `//` and `/\` are authority forms to browsers even though they start with `/`.
  if (raw.startsWith('//')) return fallback;

  let url: URL;
  try {
    url = new URL(raw, 'http://localhost');
  } catch {
    return fallback;
  }

  // Defence in depth: the parsed origin must still be the placeholder origin.
  if (url.origin !== 'http://localhost') return fallback;

  const result = `${url.pathname}${url.search}${url.hash}`;

  /**
   * RE-VALIDATE THE OUTPUT, not just the input.
   *
   * Normalisation can turn a path that passed every check above back into an
   * AUTHORITY form: `/x/../../..//evil.com/pwn` parses as a local path whose
   * origin is `localhost`, but its normalised pathname is `//evil.com/pwn` —
   * which a browser treats as protocol-relative and navigates to evil.com.
   *
   * This is not hypothetical; it is the case `tests/redirect.test.ts` was written
   * to catch. Any check that only inspects the pre-normalisation input has this
   * hole, which is why the invariants are re-applied to what is actually
   * returned.
   */
  if (result.startsWith('//') || result.startsWith('/\\')) return fallback;
  if (hasUnsafeCharacter(result)) return fallback;

  return result;
}